import React from 'react';
import Content from './Content';
import confirm from '../../utils/confirm';
import HomeChart from './HomeChart';
import Dialog from '../../toolkit/dialog';
import CustomizeDialog from './CustomizeDialog';
import prefService from '../../services/pref-service';
import alarmService from '../../services/alarm-service';
import sensorService from '../../services/sensor-service';
import SensorItem from './SensorItem';
import { Sensor } from '../interfaces';
import { getCurrentUser } from '../../utils/auth';
import { TEHRAN_COORDINATES } from '../../utils/points';
import { Map, MarkerOption } from '../../utils/openlayer/map';

import markerRedImage from '../../assets/marker-red-30.png';
import markerBlueImage from '../../assets/marker-blue-30.png';

export default class Home extends React.Component {

  private columnCount = 0;

  state = {
    prefs: {
      map: false,
      graph: false,
      alarm: false,
      group: false,
    },
    sensors: [] as Sensor[],
    alarmedSensors: []
  }

  render() {
    this.columnCount = 0;
    return (
      <Content title="خانه">
        <div className="home-component row">
          <div className="col-12">
            <button className="ant-btn customize-btn" onClick={this.handleCustomizeClick}>
              <i className="far fa-paint-roller"></i>
              <span>شخصی سازی</span>
            </button>
            <hr />
          </div>
          {this.renderChart()}
          {this.renderMap()}
          {this.renderAlarmedSensors()}
        </div>
      </Content>
    );
  }

  renderChart = () => {
    const { graph } = this.state.prefs;
    if (!graph) return '';

    this.columnCount++;
    return (
      <div className={this.getColumnClasses()}>
        <HomeChart />
      </div>
    );
  }

  renderMap = () => {
    const { map } = this.state.prefs;
    if (!map) return '';

    this.columnCount++;
    return (
      <div className={this.getColumnClasses()}>
        <div id="home-map" className="ol-map"></div>
      </div>
    );
  }

  renderAlarmedSensors = () => {
    const { alarm } = this.state.prefs;
    if (!alarm) return '';

    this.columnCount++;
    const { alarmedSensors } = this.state;
    let boxContent: JSX.Element | JSX.Element[];
    if (alarmedSensors.length === 0) {
      boxContent = (
        <div className="alert alert-primary text-center w-100 m-3">
          هیچ سنسوری در وضعیت هشدار نیست
        </div>
      );
    }
    else {
      const cols = this.isColumnFilled() ? 'col-xl-3 col-lg-4 col-md-6 col-12' : 'col-lg-6 col-12';
      boxContent = (
        alarmedSensors.map((sensor, i) => (
          <SensorItem
            key={i}
            cols={cols}
            sensor={sensor}
            onKillAlarmClicked={this.handleKillAlarmClicked}
          />
        ))
      );
    }
    return (
      <div className={this.getColumnClasses()}>
        <div className="home-box">
          <span className="home-title">سنسورهای در حال هشدار</span>
          <div className="row">
            {boxContent}
          </div>
        </div>
      </div>
    );
  }

  getColumnClasses = () => {
    if (this.columnCount === 1 || this.columnCount === 2) {
      return 'col-xxl-6 col-xl-12 col-12 mt-3';
    }
    return 'col-12 mt-3';
  }

  isColumnFilled = () => {
    return this.columnCount !== 1 && this.columnCount !== 2;
  }

  componentDidMount = async () => {
    try {
      const prefs = await prefService.getHomePrefs();
      await new Promise(resolve => this.setState({ prefs }, resolve as any));
    }
    catch (e) {
      console.error(e);
    }
    await this.refreshAlarmedSensors();
    const sensors = await sensorService.fetch();
    await new Promise(resolve => this.setState({ sensors }, resolve as any));
    if (this.state.prefs.map) {
      this.createMap();
    }
  }

  private refreshAlarmedSensors = async () => {
    const token = getCurrentUser();
    try {
      const alarmedSensors = await sensorService.fetchAlarmedSensor(token._id);
      this.setState({ alarmedSensors });
    }
    catch (e) {
      console.error(e);
    }
  }

  private createMap = () => {
    new Map({
      id: 'home-map',
      center: TEHRAN_COORDINATES,
      markers: this.provideMarkerFromSensors(),
      markerFitZoom: true,
      maxZoom: 17
    });
    document.querySelectorAll('.popup-btn').forEach(x => x.addEventListener('mouseup', this.handlePopupClick))
  }

  private provideMarkerFromSensors = () => {
    return this.state.sensors
      .filter(s => s.location && s.location.lat && s.location.lng)
      .map(s => {
        return ({
          image: (s.alarm && s.alarm.status === 1) ? markerRedImage : markerBlueImage,
          point: [s.location?.lng, s.location?.lat],
          popup: `
            <div class="home-ul-popup">
              <div>${s._id}</div>
              <hr />
              <div class="marker-buttons">
                <span class="popup-btn ant-btn ant-btn-primary" data-href="/dashboard/graph/${s._id}">
                  <i class="far fa-chart-pie"></i>
                  مشاهده گراف
                </span>
                <span
                  class="popup-btn ant-btn ant-btn-primary"
                  data-href="/dashboard/sensors/edit/${s._id}">
                  <i class="far fa-cog"></i>
                  تنظیمات
                </span>
              </div>
            </div>
          `
        }) as MarkerOption
      });
  }

  private handlePopupClick = (event: MouseEvent) => {
    const target = event.currentTarget as HTMLElement;
    const href = target.getAttribute('data-href');
    window.location.href = href;
  }

  private handleCustomizeClick = () => {
    const dialog = new Dialog({
      rtl: true,
      size: 'small',
      title: 'شخصی سازی خانه',
      message: CustomizeDialog,
      onClose: result => {
        if (result) {
          window.location.reload();
        }
      }
    });
    dialog.show();
  }

  private handleKillAlarmClicked = (sensorId: string) => {
    const dialog = confirm({
      type: 'alert',
      title: 'آیا میخواهید هشدار این سنسور را قطع کنید؟',
      primaryText: 'بله',
      primaryClass: 'ant-btn-danger',
      onSubmit: async () => {
        try {
          await alarmService.killAlarm(sensorId);
          await this.refreshAlarmedSensors();
        }
        catch (e) {
          console.error(e);
        }
      }
    });
    dialog.show();
  }

}
